import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, ChevronLeft, FileText, ArrowLeftRight, Info, XCircle, Loader2, CalendarDays, RefreshCcw } from 'lucide-react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import FastAPIClient from '../services/apiClient';
import { useToast } from '../hooks/useToast';
import { fetchAccountDetails } from '../apiHelper';

const client = new FastAPIClient();

/**
 * Customized DataTable for Ledger Display
 * Tailored for accounting ledger with Date, Reference, Description, Debit, Credit, Balance columns.
 * Supports search, no pagination, and uses Tailwind CSS with Gmail color scheme.
 */
const LedgerTable = ({ columns, data, title, classNames, onRowClick }) => {
    const [search, setSearch] = useState('');

    const filteredData = useMemo(() => {
        if (!search.trim()) return data;
        const lowerSearch = search.toLowerCase();
        return data.filter(row =>
            columns.some(col => {
                const val = col.searchable ? col.searchable(row) : row[col.key];
                return val && String(val).toLowerCase().includes(lowerSearch);
            })
        );
    }, [search, data, columns]);

    return (
        <div className={`bg-white rounded-xl shadow-sm border border-gray-200 ${classNames}`}>
            {title && (
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
                </div>
            )}
            <div className="px-6 py-4 flex items-center justify-end">
                <div className="relative w-full max-w-sm">
                    <input
                        type="text"
                        placeholder="Search ledger..."
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm w-full
                                   focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                                   transition-all duration-200 ease-in-out"
                        aria-label="Search ledger data"
                    />
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="min-w-full table-auto text-sm text-gray-900">
                    <thead className="bg-gray-100 border-b border-gray-200">
                        <tr>
                            {columns.map((col) => (
                                <th key={col.key} className="px-6 py-3 text-left font-semibold tracking-wide uppercase text-gray-500">
                                    {col.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {filteredData.length > 0 ? (
                            filteredData.map((row, i) => (
                                <motion.tr
                                    key={row.posting_id || i}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: i * 0.05 }}
                                    className={`hover:bg-gray-50 transition-colors duration-150 ease-in-out ${onRowClick && row.transaction?.orders_id ? 'cursor-pointer' : ''}`}
                                    onClick={() => onRowClick && row.transaction?.orders_id && onRowClick(row)}
                                >
                                    {columns.map((col) => (
                                        <td key={col.key} className="px-6 py-4 whitespace-nowrap">
                                            {col.render ? col.render(row[col.key], row) : row[col.key]}
                                        </td>
                                    ))}
                                </motion.tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={columns.length} className="text-center py-8 text-gray-500">
                                    No transactions available.
                                </td>
                            </tr>
                        )}
                    </tbody>
                    {/* Summary Row */}
                    {filteredData.length > 0 && (
                        <tfoot className="bg-gray-50 border-t border-gray-200">
                            <tr>
                                {columns.map((col, index) => (
                                    <td key={col.key} className="px-6 py-4 font-semibold text-gray-900">
                                        {index === 0 && 'Totals'}
                                        {col.key === 'debit' && `${filteredData[0]?.currency || '₦'}${filteredData.reduce((sum, row) => sum + (row.debit || 0), 0).toFixed(2)}`}
                                        {col.key === 'credit' && `${filteredData[0]?.currency || '₦'}${filteredData.reduce((sum, row) => sum + (row.credit || 0), 0).toFixed(2)}`}
                                        {col.key === 'balance' && `${filteredData[0]?.currency || '₦'}${filteredData[filteredData.length - 1]?.balance.toFixed(2) || '0.00'}`}
                                    </td>
                                ))}
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        </div>
    );
};

/**
 * AccountLedgerPage Component.
 * Displays a ledger view with debits, credits, running balance, and summaries for a specific accounting account.
 * Structured per Sage Accounting ledger format: Date, Reference, Description, Debit, Credit, Balance.
 * Uses actual postings data without assuming opposite entries.
 */
const AccountLedgerPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { showToast } = useToast();

    const accountId = location.state?.accountId;
    const accountName = location.state?.accountName || 'Account Ledger';
    const user = location.state?.user;

    const [postings, setPostings] = useState([]);
    const [accountType, setAccountType] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [loggedInUser, setLoggedInUser] = useState(null);
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30); // Default to last 30 days
        return d.toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

    // Effect to load user from local storage if not passed via state
    useEffect(() => {
        if (!user) {
            const lcUser = localStorage.getItem('user');
            if (!lcUser) {
                showToast('Please log in to view account ledger.', 'info');
                navigate('/login');
            } else {
                setLoggedInUser(JSON.parse(lcUser));
            }
        } else {
            setLoggedInUser(user);
        }
    }, [user, showToast, navigate]);

    // Fetch account details (postings and account type) when component mounts or accountId/loggedInUser/startDate/endDate changes
    const fetchLedgerData = useMemo(() => {
        if (!accountId || !loggedInUser) return null;

        return async () => {
            setLoading(true);
            setError(null);
            try {
                const params = {
                    account_id: accountId,
                    start_date: startDate,
                    end_date: endDate
                };
                // console.log(params)
                const fetchedData = await fetchAccountDetails(params, loggedInUser);
                if (fetchedData && Array.isArray(fetchedData.postings) && fetchedData.account_type) {
                    setPostings(fetchedData.postings);
                    setAccountType(fetchedData.account_type);
                } else {
                    setPostings([]);
                    setAccountType('');
                    showToast('Unexpected data structure received for account ledger.', 'error');
                }
            } catch (err) {
                console.error(`Error loading ledger for account ${accountId}:`, err.response);
                const errorMessage = err.response?.data?.detail || `Failed to load ledger for ${accountName}.`;
                setError(errorMessage);
                showToast(errorMessage, 'error');
            } finally {
                setLoading(false);
            }
        };
    }, [accountId, accountName, loggedInUser, startDate, endDate, showToast]);

    useEffect(() => {
        if (fetchLedgerData) {
            fetchLedgerData();
        }
    }, [fetchLedgerData]);

    // Process postings: sort by date, compute running balance based on account type
    const processedPostings = useMemo(() => {
        if (!postings.length || !accountType) return [];

        const sorted = [...postings].sort((a, b) =>
            new Date(a.transaction.transaction_date) - new Date(b.transaction.transaction_date)
        );

        const normalDebit = ['asset', 'expense'].includes(accountType.toLowerCase());

        let balance = 0;
        return sorted.map(p => {
            const amount = parseFloat(p.amount) || 0;
            const delta = normalDebit
                ? (p.posting_type === 'debit' ? amount : -amount)
                : (p.posting_type === 'credit' ? amount : -amount);
            balance += delta;
            return {
                ...p,
                debit: p.posting_type === 'debit' ? amount : 0,
                credit: p.posting_type === 'credit' ? amount : 0,
                balance,
                currency: p.transaction?.currency_code || '₦'
            };
        });
    }, [postings, accountType]);

    // Define columns for the ledger table
    const ledgerColumns = useMemo(() => [
        {
            key: 'transaction.transaction_date',
            label: 'Date',
            searchable: (row) => {
                try {
                    return new Date(row.transaction.transaction_date).toLocaleDateString();
                } catch {
                    return '';
                }
            },
            render: (value, row) => {
                try {
                    return new Date(row.transaction.transaction_date).toLocaleDateString(undefined, {
                        year: 'numeric', month: 'short', day: 'numeric'
                    });
                } catch {
                    return 'Invalid Date';
                }
            }
        },
        {
            key: 'transaction.ref',
            label: 'Reference',
            searchable: (row) => row.transaction?.ref,
            render: (value, row) => (
                <div className="flex items-center gap-1">
                    <FileText size={14} className="text-gray-500" />
                    <span className="font-mono text-xs">{row.transaction.ref || 'N/A'}</span>
                </div>
            )
        },
        {
            key: 'transaction.description',
            label: 'Description',
            searchable: (row) => row.transaction?.description,
            render: (value, row) => (
                <div className="whitespace-normal break-words max-w-md">
                    {row.transaction.description || 'N/A'}
                </div>
            )
        },
        {
            key: 'debit',
            label: 'Debit',
            searchable: (row) => row.debit ? row.debit.toFixed(2) : '',
            render: (debit, row) => debit > 0 ? `${row.currency}${debit.toFixed(2)}` : ''
        },
        {
            key: 'credit',
            label: 'Credit',
            searchable: (row) => row.credit ? row.credit.toFixed(2) : '',
            render: (credit, row) => credit > 0 ? `${row.currency}${credit.toFixed(2)}` : ''
        },
        {
            key: 'balance',
            label: 'Balance',
            searchable: (row) => row.balance.toFixed(2),
            render: (balance, row) => `${row.currency}${balance.toFixed(2)}`
        }
    ], []);

    // Handle row click for navigation to order details
    const handleRowClick = (row) => {
        if (row.transaction?.orders_id) {
            navigate('/accounting/order-details', {
                state: { ordersId: row.transaction.orders_id, user: loggedInUser }
            });
        }
    };

    // Handle date change
    const handleDateChange = (setter) => (e) => {
        setter(e.target.value);
    };

    if (!loggedInUser) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-orange-600">
                <Info size={32} className="mb-2" />
                <p className="text-lg mb-4">Please log in to view account ledger.</p>
            </div>
        );
    }

    if (!accountId) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-red-600">
                <XCircle size={32} className="mb-2" />
                <p className="text-lg mb-4">No account selected. Please go back to Accounting Dashboard.</p>
                <Button onClick={() => navigate('/accounting')} variant="primary" className="mt-4">
                    <ChevronLeft size={20} className="mr-2" /> Go to Accounting
                </Button>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
            <Card className="p-4 sm:p-6 shadow-lg rounded-lg">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <ArrowLeftRight size={24} className="text-blue-600" /> Account Ledger: {accountName}
                    </h2>
                    <Button className="flex items-center justify-center px-4 py-3" onClick={() => navigate('/accounting')} variant="secondary" size="sm">
                        <ChevronLeft size={20} className="mr-2" /> Back to Accounting
                    </Button>
                </div>
                <p className="text-gray-600 mb-6">
                    Detailed ledger view of transactions for this account ({accountType}) from {startDate} to {endDate}.
                </p>
                {/* Filters and Controls */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-8">
                    <div className="flex items-center gap-4 w-full sm:w-auto">
                        <div className="flex items-center gap-2 w-full">
                            <label htmlFor="startDate" className="text-sm font-medium text-gray-700">From:</label>
                            <div className="relative w-full">
                                <input
                                    type="date"
                                    id="startDate"
                                    value={startDate}
                                    onChange={handleDateChange(setStartDate)}
                                    className="block w-full rounded-md border-gray-300 shadow-sm pl-9 pr-3 py-2 text-gray-900 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                />
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <CalendarDays className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 w-full">
                            <label htmlFor="endDate" className="text-sm font-medium text-gray-700">To:</label>
                            <div className="relative w-full">
                                <input
                                    type="date"
                                    id="endDate"
                                    value={endDate}
                                    onChange={handleDateChange(setEndDate)}
                                    className="block w-full rounded-md border-gray-300 shadow-sm pl-9 pr-3 py-2 text-gray-900 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                />
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <CalendarDays className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <Button onClick={fetchLedgerData} disabled={loading} className="flex items-center justify-center w-full sm:w-auto">
                        {loading ? <Loader2 className="animate-spin" size={20} /> : <RefreshCcw size={20} />}
                        <span className="ml-2">Refresh Ledger</span>
                    </Button>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="animate-spin h-6 w-6 mr-2 text-blue-500" />
                        <p className="text-gray-600">Loading account ledger...</p>
                    </div>
                ) : error ? (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-4" role="alert">
                        <p>{error}</p>
                    </div>
                ) : (
                    <LedgerTable
                        columns={ledgerColumns}
                        data={processedPostings}
                        title="Transaction Ledger"
                        className="mb-6"
                        onRowClick={handleRowClick}
                    />
                )}
            </Card>
        </div>
    );
};

export default AccountLedgerPage;